<script lang="ts" setup>
import { computed, Ref, ref } from "vue";
import PicIcon from "@/components/icon/PicIcon.vue";
import VideoIcon from "@/components/icon/VideoIcon.vue";
import { isImage } from "@/modules/common";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import { useTask } from "@/stores/task";
import BaseImg from "../common/BaseImg.vue";
// 1. 常量
// 2. Props & Emit & Expose

const taskStore = useTask();

export interface UniqueMedia extends Media {
  uniqueId?: number;
}

export interface GenMedia extends Media {
  textImage?: Omit<TextImage, "media">;
}

export interface GalleryInstance {
  clear: (clearFilter?: boolean) => void;
  selectedMediaList: Ref<UniqueMedia[]>;
  resetScrollLoadingState: () => void;
}

const props = withDefaults(
  defineProps<{
    filter?: 1 | 2; // 1-图片；2-视频
    selectable?: boolean;
    enableUpload?: boolean;
    maxSelectCount?: number;
    mediaList?: UniqueMedia[];
    onPullLoad?: () => Promise<any>;
    onScrollLoad?: () => Promise<boolean>;
    enablePreviewInfo?: boolean;
    enableDelete?: boolean;
    useUniqueId?: boolean;
  }>(),
  {
    selectable: false,
    enableUpload: false,
    maxSelectCount: 1,
    mediaList: () => [] as UniqueMedia[],
    onPullLoad: async () => {},
    onScrollLoad: async () => false,
    enablePreviewInfo: true,
    enableDelete: false,
    useUniqueId: false,
  }
);
const emit = defineEmits<{
  (e: "afterDelete", mediaId: number): void;
}>();
// 3. 内部状态
const currentFilter = ref<typeof props.filter | null>(props.filter ?? null); // null: 全部, 1: 图片, 2: 视频
const listRef = ref<ThreadListInstance | null>(null);
// 实际渲染的媒体列表（根据过滤器）
const filteredMediaList = computed(() => {
  if (currentFilter.value === 1)
    return props.mediaList.filter((item) => isImage(item.url));
  else if (currentFilter.value === 2)
    return props.mediaList.filter((item) => !isImage(item.url));
  else return props.mediaList;
});
// 预览遮罩层状态
const isShowPreview = ref(false);
const startPosition = ref(0);
// 选择状态
const selectedIdSet = ref<Set<number>>(new Set());
const selectedMediaList = computed(() => {
  let _selectedMediaList: GenMedia[] = [];
  for (const id of selectedIdSet.value) {
    const item = taskStore.galleryMap.get(id);
    if (item) {
      _selectedMediaList.push(item);
    } else {
      _selectedMediaList = props.mediaList.filter((item) =>
        selectedIdSet.value.has(props.useUniqueId ? item.uniqueId! : item.id!)
      );
    }
  }

  return _selectedMediaList;
});
// 4. Lifecycle

// 5. Methods
async function clear(clearFilter = false) {
  selectedIdSet.value.clear();
  if (clearFilter) {
    currentFilter.value = null;
  }
  listRef.value?.resetScrollLoadingState();
}

const onSelect = (idx: number) => {
  if (props.selectable) {
    const target = filteredMediaList.value[idx];
    const key = getKey(target);
    if (props.maxSelectCount === 1) selectedIdSet.value.clear();
    if (
      !selectedIdSet.value.has(key!) &&
      selectedMediaList.value.length >= props.maxSelectCount
    )
      return;
    if (selectedIdSet.value.has(key!)) {
      selectedIdSet.value.delete(key!);
    } else {
      selectedIdSet.value.add(key!);
    }
  } else {
    isShowPreview.value = true;
    startPosition.value = idx;
  }
};
const getKey = (item: UniqueMedia) =>
  props.useUniqueId ? item.uniqueId! : item.id!;
defineExpose<GalleryInstance>({
  clear, // method: 刷新gallery列表
  resetScrollLoadingState: () => listRef.value?.resetScrollLoadingState(),
  selectedMediaList, // computed: 已选中的媒体列表
});
</script>
<template>
  <div class="flex flex-col gap-2 h-full overflow-hidden">
    <div v-if="filter === undefined" class="flex justify-between items-center">
      <div class="tabs tabs-boxed w-fit">
        <a
          :class="{ 'tab-active': currentFilter === null }"
          class="tab"
          @click="currentFilter = null"
        >
          {{ $t("User.gallery.all") }}
        </a>
        <a
          :class="{ 'tab-active': currentFilter === 1 }"
          class="tab"
          @click="currentFilter = 1"
        >
          <i class="iconify mingcute--pic-line" />
        </a>
        <a
          :class="{ 'tab-active': currentFilter === 2 }"
          class="tab"
          @click="currentFilter = 2"
        >
          <i class="iconify mingcute--video-camera-line" />
        </a>
      </div>
    </div>
    <thread-list
      ref="listRef"
      :on-pull-load="onPullLoad"
      :on-scroll-load="onScrollLoad"
      class="h-full"
    >
      <div class="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-0.5">
        <div
          v-for="(item, idx) in filteredMediaList"
          :key="item.id"
          class="w-full"
          @click="onSelect(idx)"
        >
          <div
            v-if="isImage(item.url)"
            class="bg-base-200 w-full h-full relative flex items-center"
          >
            <BaseImg :url="item.url" :width="200" />
            <div
              v-if="selectedIdSet.has(getKey(item))"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedIdSet.has(getKey(item))"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
          <div v-else class="w-full h-full bg-base-200 relative">
            <video
              :poster="item.videoCoverUrl"
              :src="item.url"
              class="w-full h-full"
              crossorigin="anonymous"
            ></video>
            <div class="absolute right-1 bottom-1 rounded">
              <i
                class="iconify mingcute--video-camera-line text-base-content/50"
              />
            </div>
            <div
              v-if="selectedIdSet.has(getKey(item))"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedIdSet.has(getKey(item))"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
        </div>
      </div>
    </thread-list>
    <div v-if="false">
      <slot name="empty">
        <div class="flex flex-col items-center justify-center h-[200px]">
          <div class="flex flex-col items-center justify-center">
            <div class="text-2xl font-bold">
              {{ $t("Texts.stillNothingTheAlbum") }}
            </div>
          </div>
        </div>
      </slot>
    </div>
    <mate-media-preview
      v-model:show="isShowPreview"
      :enable-delete="enableDelete"
      :mate-media-list="mediaList"
      :start-position="startPosition"
      @after-delete="emit('afterDelete', $event)"
    />
  </div>
</template>
